import type { DocumentResult, ProcessResult } from '../shared/desktop-api';

export type BoundaryDocumentMutation = {
  position: number;
  removedDocumentIds: readonly string[];
  documents: readonly DocumentResult[];
};

/**
 * Apply one positional document mutation without replacing the rest of a live
 * batch snapshot.
 *
 * Boundary writes are positional in the engine, but field and line-item saves
 * on unrelated documents may finish while the boundary IPC is in flight. A
 * spread of the click-time ProcessResult would put those older document objects
 * back. Matching the exact identities at the mutation site makes this a small,
 * fail-closed splice and leaves every unrelated live object untouched.
 */
export const spliceBoundaryDocuments = (
  batch: ProcessResult,
  mutation: BoundaryDocumentMutation,
): ProcessResult => {
  const { position, removedDocumentIds, documents } = mutation;
  if (position < 0 || position + removedDocumentIds.length > batch.documents.length)
    throw new Error('The documents around this boundary moved. Reload them before trying again.');
  const actualIds = batch.documents
    .slice(position, position + removedDocumentIds.length)
    .map((document) => document.id);
  if (
    actualIds.length !== removedDocumentIds.length ||
    actualIds.some((id, index) => id !== removedDocumentIds[index])
  )
    throw new Error('The documents around this boundary changed. Reload them before trying again.');
  return {
    ...batch,
    documents: [
      ...batch.documents.slice(0, position),
      ...documents,
      ...batch.documents.slice(position + removedDocumentIds.length),
    ],
  };
};

/** The exact inverse needed to restore only the documents a mutation replaced. */
export const invertBoundaryMutation = (
  previous: ProcessResult,
  mutation: BoundaryDocumentMutation,
): BoundaryDocumentMutation => ({
  position: mutation.position,
  removedDocumentIds: mutation.documents.map((document) => document.id),
  documents: previous.documents.slice(
    mutation.position,
    mutation.position + mutation.removedDocumentIds.length,
  ),
});
